import React, { useState, useEffect } from 'react';
import { Users, Search, Phone, Mail, DollarSign, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const EngineerClients = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedEngineers, setExpandedEngineers] = useState({});

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/employees/clients`);
      setData(res.data.engineers || []);
      // Expand all by default
      const expanded = {};
      (res.data.engineers || []).forEach(e => { expanded[e.engineer_id] = true; });
      setExpandedEngineers(expanded);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleEngineer = (id) => {
    setExpandedEngineers(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredData = data.map(eng => ({
    ...eng,
    clients: eng.clients.filter(c =>
      !search ||
      (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search)
    )
  })).filter(eng => eng.clients.length > 0);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 mt-4">Loading client lists...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="engineer-clients-panel">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Users size={20} className="text-purple-400" />
          Engineer Client Lists
        </h2>
        <div className="relative w-full sm:w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 outline-none"
            data-testid="client-search-input"
          />
        </div>
      </div>

      {filteredData.map(eng => (
        <div key={eng.engineer_id} className="bg-gray-900/60 border border-gray-700 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleEngineer(eng.engineer_id)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800/50 transition-colors"
            data-testid={`engineer-toggle-${eng.engineer_id}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold text-white">{eng.engineer_name}</span>
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                {eng.clients.length} clients
              </span>
            </div>
            {expandedEngineers[eng.engineer_id] ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
          </button>

          {expandedEngineers[eng.engineer_id] && (
            <div className="border-t border-gray-700">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs uppercase tracking-wider">
                      <th className="text-left px-4 py-2">Client</th>
                      <th className="text-left px-4 py-2 hidden sm:table-cell">Contact</th>
                      <th className="text-center px-4 py-2">Sessions</th>
                      <th className="text-right px-4 py-2">Spent</th>
                      <th className="text-right px-4 py-2 hidden sm:table-cell">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eng.clients.map((client, idx) => (
                      <tr key={idx} className="border-t border-gray-800 hover:bg-gray-800/30">
                        <td className="px-4 py-2.5">
                          <p className="text-white font-medium">{client.name}</p>
                          <p className="text-gray-500 text-xs sm:hidden">{client.email}</p>
                        </td>
                        <td className="px-4 py-2.5 hidden sm:table-cell">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-gray-300 text-xs flex items-center gap-1">
                              <Mail size={10} /> {client.email}
                            </span>
                            {client.phone && (
                              <span className="text-gray-400 text-xs flex items-center gap-1">
                                <Phone size={10} /> {client.phone}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className="text-white font-semibold">{client.total_bookings}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="text-green-400 font-semibold">${client.total_spent?.toFixed(0)}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right hidden sm:table-cell">
                          <span className="text-gray-400 text-xs">{client.last_booking}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default EngineerClients;
